import { isError } from "@sindresorhus/is";
import type { TCountryCode } from "countries-list";
import AddToCartButton from "../../components/AddToCartButton";
import type { ProductCardProps } from "../../components/ProductCard/ProductCard";
import ImageContainer from "../../components/general/ImageContainers";
import Link from "../../components/general/Link";
import OutOfStockLabel from "../../components/general/OutOfStockLabel";
import VariantList from "../../components/general/VariantList";
import VariantListItem from "../../components/general/VariantList/components/VariantListItem";
import type { CustomLineItemData } from "../../hooks/use-cart-service";
import { isOnStock } from "../../utils/commercetools/availability";
import { mapProductVariant } from "../commercetools/product-list";
import { notNull, notUndefined } from "../type-guards/general";

const mapVariantSymbolListItem = (
	variant: Queries.ProductVariantFragment | null,
) => {
	const image = variant?.variantSymbolImage;
	return image ? (
		<VariantListItem
			key={`${variant.id}-${image.src}`}
			image={{ src: image.src ?? "", alt: image.alt ?? "" }}
		/>
	) : undefined;
};

const mapVariantsToVariantSymbolList = (
	variants: Queries.ProductDataFragment["variants"],
	buttonType: "icon" | "standard",
	onClick?: () => Promise<void>,
	buttonText?: string,
	outOfStockLabel?: string,
) => {
	return (
		<>
			<VariantList
				variantAmount={variants.length > 0 ? variants.length - 1 : 0}
			>
				{variants
					.slice(0, 5)
					.map((variant) => mapVariantSymbolListItem(variant))}
			</VariantList>
			{buttonText && onClick ? (
				<AddToCartButton
					label={buttonText}
					onClick={onClick}
					buttonType={buttonType}
				/>
			) : (
				outOfStockLabel && <OutOfStockLabel label={outOfStockLabel} />
			)}
		</>
	);
};

const mapProductVariantToProductCardProps = (
	onClick: () => Promise<void>,
	otherVariants: Queries.ProductVariantFragment[],
	currency: string,
	buttonType: "icon" | "standard",
	country?: TCountryCode,
	productName?: string | null,
	variant?: Queries.ProductVariantFragment | null,
	label?: string | null,
	url?: string,
	buttonText?: string,
	ariaLabelLink?: string,
	outOfStockLabel?: string,
	productType?: string,
	compactCard?: boolean,
): ProductCardProps | undefined => {
	const masterVariant =
		variant && mapProductVariant(variant, currency, country);
	if (!masterVariant) {
		return undefined;
	}

	const { color, fabric, images, rawPrice } = masterVariant;

	const image = images[0];
	const altImage = images[1];

	if (!color || !productName || !image) {
		return undefined;
	}

	return {
		color: color,
		fabric: fabric ?? undefined,
		originalImages: images,
		image: (
			<ImageContainer
				alt={image.alt ?? productName}
				ratio="portrait"
				objectFit={image.addPadding ? "contain" : "cover"}
				imgSrc={image.src}
				addPadding={image.addPadding ?? undefined}
				paddingVariant="productCard"
				sizes="(max-width: 768px) 80vw, 20vw"
				hidePlaceholder
			/>
		),
		altImage: altImage ? (
			<ImageContainer
				alt={altImage.alt ?? productName}
				ratio="portrait"
				objectFit={altImage.addPadding ? "contain" : "cover"}
				imgSrc={altImage.src}
				addPadding={compactCard ? false : (altImage.addPadding ?? undefined)}
				paddingVariant="productCard"
				sizes="(max-width: 768px) 80vw, 20vw"
				hidePlaceholder
			/>
		) : undefined,
		name: productName,
		rawPrice: rawPrice,
		isNew: Boolean(label),
		newLabel: label ?? undefined,
		children: mapVariantsToVariantSymbolList(
			otherVariants,
			buttonType,
			isOnStock(variant) ? onClick : undefined,
			buttonText,
			outOfStockLabel,
		),
		linkComponent: (children) => (
			<Link href={url} aria-label={ariaLabelLink}>
				{children}
			</Link>
		),
		url: url,
		isOnStock: isOnStock(variant),
		productType,
	};
};

type MapStoryblokVariantToProductCardProps = {
	variantPage: Queries.StoryblokVariantProductFragment | null;
	addItemsToCart?: (
		items: {
			product: Queries.CommerceToolsProductFragment;
			variant: Queries.ProductVariantFragment;
			additionalData?: CustomLineItemData;
		}[],
	) => Promise<void>;
	currency: string;
	country?: TCountryCode;
	ariaLabelLink?: string;
	outOfStockLabel?: string;
	buttonText?: string;
	buttonType?: "icon" | "standard";
	compactCard?: boolean;
};

export const mapStoryblokVariantToProductCard = ({
	variantPage,
	addItemsToCart,
	currency,
	country,
	ariaLabelLink,
	outOfStockLabel,
	buttonText,
	buttonType = "icon",
	compactCard = false,
}: MapStoryblokVariantToProductCardProps) => {
	if (
		!variantPage ||
		!variantPage.commercetools_variant ||
		!variantPage.commercetools_variant.product
	) {
		return;
	}

	const addToCart = async () => {
		if (!otherVariants[0] || !addItemsToCart) return;
		try {
			await addItemsToCart([{ product, variant: otherVariants[0] }]);
		} catch (error) {
			throw isError(error)
				? error
				: new Error("An error occured while adding an item to the cart");
		}
	};

	const product = variantPage.commercetools_variant.product;
	const commercetoolsVariant = variantPage.commercetools_variant;

	const masterVariant = product.masterVariant ?? undefined;

	const otherVariants = [masterVariant, ...product.variants]
		.filter((variant) => variant?.id !== commercetoolsVariant.id)
		.filter(notUndefined)
		.filter(notNull);

	const productType =
		variantPage.commercetools_variant.product.productType.obj.key;

	const url =
		commercetoolsVariant.storyblok_variant_pages?.[0]?.full_slug ??
		variantPage.full_slug ??
		undefined;

	const productCardProps = mapProductVariantToProductCardProps(
		addToCart,
		otherVariants,
		currency,
		buttonType,
		country,
		product.name,
		commercetoolsVariant,
		commercetoolsVariant.storyblok_variant_pages?.[0]?.field_label_string,
		url,
		buttonText,
		ariaLabelLink,
		outOfStockLabel,
		productType,
		compactCard,
	);

	return productCardProps;
};
